import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transformKeys } from '../services/transformers';

interface CampaignCallObserverEvent {
  campaignId?: string;
}

interface CampaignContactObserverEvent {
  campaignId?: string;
  campaign?: string;
}

interface CampaignRuntimeEventEnvelope {
  payload?: {
    campaignId?: string;
    campaign?: string;
  };
  scope?: {
    campaignId?: string;
  };
}

interface UseCampaignRealtimeListRefreshParams {
  campaignId?: string;
  wsConnected: boolean;
  isCallsTab: boolean;
  onRefreshContacts: () => void;
  onRefreshCalls: () => void;
  throttleMs?: number;
}

interface ScheduleOptions {
  immediate?: boolean;
  incrementBadge?: boolean;
}

interface ScheduleFlags {
  contacts?: boolean;
  calls?: boolean;
}

export function useCampaignRealtimeListRefresh({
  campaignId,
  wsConnected,
  isCallsTab,
  onRefreshContacts,
  onRefreshCalls,
  throttleMs = 1500,
}: UseCampaignRealtimeListRefreshParams) {
  const { subscribe } = useWebSocket();
  const [pendingContactsUpdates, setPendingContactsUpdates] = useState(0);
  const [pendingCallsUpdates, setPendingCallsUpdates] = useState(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushPendingListRefreshRef = useRef<() => void>(() => undefined);
  const isCallsTabRef = useRef(isCallsTab);
  const onRefreshContactsRef = useRef(onRefreshContacts);
  const onRefreshCallsRef = useRef(onRefreshCalls);
  const pendingRefreshRef = useRef<{ contacts: boolean; calls: boolean }>({
    contacts: false,
    calls: false,
  });

  useEffect(() => {
    isCallsTabRef.current = isCallsTab;
  }, [isCallsTab]);

  useEffect(() => {
    onRefreshContactsRef.current = onRefreshContacts;
  }, [onRefreshContacts]);

  useEffect(() => {
    onRefreshCallsRef.current = onRefreshCalls;
  }, [onRefreshCalls]);

  const flushPendingListRefresh = useCallback(() => {
    refreshTimerRef.current = null;
    const shouldRefreshContacts = pendingRefreshRef.current.contacts;
    const shouldRefreshCalls = pendingRefreshRef.current.calls;
    pendingRefreshRef.current.contacts = false;
    pendingRefreshRef.current.calls = false;

    if (shouldRefreshContacts) {
      if (!isCallsTabRef.current) {
        setPendingContactsUpdates(0);
        onRefreshContactsRef.current();
      } else {
        pendingRefreshRef.current.contacts = true;
      }
    }
    if (shouldRefreshCalls) {
      if (isCallsTabRef.current) {
        setPendingCallsUpdates(0);
        onRefreshCallsRef.current();
      } else {
        pendingRefreshRef.current.calls = true;
      }
    }
  }, []);

  const scheduleRefresh = useCallback(
    (next: ScheduleFlags, options?: ScheduleOptions) => {
      const incrementBadge = options?.incrementBadge ?? true;
      if (next.contacts) {
        const wasPending = pendingRefreshRef.current.contacts;
        pendingRefreshRef.current.contacts = true;
        if (incrementBadge && !wasPending) {
          setPendingContactsUpdates((value) => Math.min(value + 1, 99));
        }
      }
      if (next.calls) {
        const wasPending = pendingRefreshRef.current.calls;
        pendingRefreshRef.current.calls = true;
        if (incrementBadge && !wasPending) {
          setPendingCallsUpdates((value) => Math.min(value + 1, 99));
        }
      }

      if (options?.immediate) {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
        flushPendingListRefresh();
        return;
      }

      if (!refreshTimerRef.current) {
        refreshTimerRef.current = setTimeout(() => {
          flushPendingListRefreshRef.current();
        }, throttleMs);
      }
    },
    [flushPendingListRefresh, throttleMs],
  );

  useEffect(() => {
    flushPendingListRefreshRef.current = flushPendingListRefresh;
  }, [flushPendingListRefresh]);

  useEffect(() => {
    if (!campaignId || !wsConnected) {
      return;
    }

    const shouldRefreshFromStream = (action: string) =>
      action === 'create' || action === 'update' || action === 'delete' || action === 'event';

    const callSubscription = subscribe(
      'call',
      { data: { campaign_id: campaignId } },
      (rawEvent: CampaignCallObserverEvent, action: string) => {
        if (!shouldRefreshFromStream(action)) return;
        const event = transformKeys<CampaignCallObserverEvent>(rawEvent);
        if (event.campaignId && event.campaignId !== campaignId) return;
        if (isCallsTabRef.current) {
          scheduleRefresh({ calls: true });
          return;
        }
        scheduleRefresh({ contacts: true });
      },
    );

    const contactSubscription = subscribe(
      'contact',
      { data: { campaign_id: campaignId } },
      (rawEvent: CampaignContactObserverEvent, action: string) => {
        if (!shouldRefreshFromStream(action)) return;
        const eventCampaignId = action === 'event'
          ? (() => {
            const event = transformKeys<CampaignRuntimeEventEnvelope>(rawEvent);
            return event.scope?.campaignId || event.payload?.campaignId || event.payload?.campaign;
          })()
          : (() => {
            const event = transformKeys<CampaignContactObserverEvent>(rawEvent);
            return event.campaignId || event.campaign;
          })();
        if (eventCampaignId && eventCampaignId !== campaignId) return;
        if (isCallsTabRef.current) {
          scheduleRefresh({ calls: true });
          return;
        }
        scheduleRefresh({ contacts: true });
      },
    );

    return () => {
      callSubscription?.unsubscribe?.();
      contactSubscription?.unsubscribe?.();
    };
  }, [campaignId, scheduleRefresh, subscribe, wsConnected]);

  useEffect(() => {
    if (isCallsTab && pendingRefreshRef.current.calls) {
      scheduleRefresh({ calls: true }, { immediate: true, incrementBadge: false });
      return;
    }
    if (!isCallsTab && pendingRefreshRef.current.contacts) {
      scheduleRefresh({ contacts: true }, { immediate: true, incrementBadge: false });
    }
  }, [isCallsTab, scheduleRefresh]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  return {
    pendingContactsUpdates,
    pendingCallsUpdates,
    scheduleRefresh,
  };
}

export default useCampaignRealtimeListRefresh;
